import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/types/database.types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

// Chrome's HTTP/2 connection pool reuses stale connections across page reloads.
// POST requests (auth token calls) are not auto-retried by the browser, so they
// silently fail with ERR_EMPTY_RESPONSE. This wrapper retries on network failure,
// which forces Chrome to open a fresh connection on the second attempt.
//
// Separately, PostgREST occasionally rejects a just-minted access token with
// 401 "JWT issued at future" — the token's `iat` is momentarily ahead of the
// clock on whichever edge node handles the request, right after a token
// refresh. It's transient infra clock skew, not an actual auth failure, and
// clears itself within a second — so a single short-delay retry is enough.
async function isJwtIssuedAtFuture(response: Response): Promise<boolean> {
  if (response.status !== 401) return false;
  const authHeader = response.headers.get('www-authenticate') ?? '';
  if (authHeader.includes('JWT issued at future')) return true;
  try {
    const body = await response.clone().text();
    return body.includes('JWT issued at future');
  } catch {
    return false;
  }
}

function fetchWithRetry(maxRetries = 3): typeof fetch {
  return async (input, init) => {
    let lastError: unknown;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await fetch(input, init);
        if (await isJwtIssuedAtFuture(response)) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          return await fetch(input, init);
        }
        return response;
      } catch (err) {
        lastError = err;
        if (attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, 150 * (attempt + 1)));
        }
      }
    }
    throw lastError;
  };
}

// @supabase/supabase-js v2 does NOT forward `auth.lockAcquireTimeout` to
// GoTrueClient (_initSupabaseAuthClient omits it from the destructure). The
// hardcoded SDK default is 5 000 ms, which fires steal:true on page reload
// while the old page still holds the navigator.lock — causing a cascade of
// NavigatorLockAcquireTimeoutError on Chrome/Edge.
//
// The `lock` option IS forwarded, so we supply a no-steal replacement: it
// calls navigator.locks.request without any signal or timeout, waiting
// indefinitely. The browser releases the old page's lock in <100 ms when it
// destroys the navigation context, so the new page acquires it with no
// observable delay and zero errors.
async function noStealLock<R>(
  name: string,
  _acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> {
  return navigator.locks.request(name, { mode: 'exclusive' }, () => fn()) as Promise<R>;
}

// Singleton stored on globalThis so Vite HMR re-evaluations reuse the same
// client instance — multiple instances fight over the same navigator.lock
// and throw LockAcquireTimeoutError.
const GLOBAL_KEY = '__supabase_singleton__';
const g = globalThis as typeof globalThis & { [GLOBAL_KEY]?: SupabaseClient<Database> };

if (!g[GLOBAL_KEY]) {
  g[GLOBAL_KEY] = createClient<Database>(supabaseUrl, supabaseKey, {
    auth: {
      lock: noStealLock,
    },
    global: {
      fetch: fetchWithRetry(3),
    },
  });
}

export const supabase: SupabaseClient<Database> = g[GLOBAL_KEY]!;
